import React, { useEffect, useState, useRef } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';

import { useConfigStore, useAppStore } from '../../stores';
import { getAuthClient } from '../../utils/auth';
import { buildYourTourApiUrl } from '../../utils/ApiHelpers';
import { getDataVersion } from '../../utils/dataVersions';
import { prefetchFloorPlans } from '../FloorPlansPage/prefetchFloorPlans';
import { markMediaVisited } from './prefetchMedia';
import { prefetchAmenities } from '../AmenitiesPage/prefetchAmenities';

import HorizontalNavBarItem from '../HorizontalNavBar/HorizontalNavBarItem';
import FadingContainer from '../FadingContainer/FadingContainer';
import MediaPagePhotoGalleryList from './MediaPagePhotoGalleryList';
import MediaPageVirtualTourList from './MediaPageVirtualTourList';
import MediaPageVideoList from './MediaPageVideoList';
import MediaPageSkeleton from './MediaPageSkeleton';
import MediaPageNavSkeleton from './MediaPageNavSkeleton';

import './MediaPage.scss';
import '../../GlobalScss/grid.scss';

const auth = getAuthClient();

const MediaPage = ({ location, history }) => {
  const [photoGalleryData, setPhotoGalleryData] = useState(null);
  const [videoData, setVideoData] = useState(null);
  const [virtualTourData, setVirtualTourData] = useState(null);
  const [navMarkup, setNavMarkup] = useState(false);
  const prefetchTriggeredRef = useRef(false);

  const config = useConfigStore((state) => state.config);
  const settings = useAppStore((state) => state.settings);
  const { communityNid, urlSlug } = config || {};
  const { mode } = settings || {};

  // Maintain url params.
  const { search } = useLocation();

  // Mark that user has visited this page (for prefetch optimization)
  useEffect(() => {
    markMediaVisited();
  }, []);

  // Fetch all media data in a single optimized API call.
  useEffect(() => {
    const fetchData = async () => {
      if (!urlSlug) return;

      try {
        const version = await getDataVersion( urlSlug, 'media', undefined, mode );
        const params = {
          ...( mode ? { mode } : {} ),
          ...( version ? { v: version } : {} ),
        };
        const url = buildYourTourApiUrl( `/your_tour_api/media_page/${urlSlug}`, params );
        const data = await auth.fetchWithAuthentication(url, 'GET', null);

        if (data && !data.error) {
          // API returns data in the exact format React expects
          setPhotoGalleryData(data.photoGalleries || []);
          setVirtualTourData(data.virtualTours || []);
          setVideoData(data.videos || []);
        } else {
          setPhotoGalleryData([]);
          setVirtualTourData([]);
          setVideoData([]);
        }
      } catch (error) {
        // Set empty arrays on error so page doesn't hang
        setPhotoGalleryData([]);
        setVirtualTourData([]);
        setVideoData([]);
      }

      // Prefetch Floor Plans and Amenities after media data loads (if not already visited)
      if (!prefetchTriggeredRef.current) {
        prefetchTriggeredRef.current = true;
        const prefetchCallback = () => {
          // Prefetch both page types in parallel for instant navigation
          prefetchFloorPlans(communityNid, urlSlug, mode);
          prefetchAmenities(urlSlug, mode);
        };
        // Use requestIdleCallback with setTimeout fallback for Safari
        if (typeof requestIdleCallback !== 'undefined') {
          requestIdleCallback(prefetchCallback, { timeout: 2000 });
        } else {
          setTimeout(prefetchCallback, 100);
        }
      }
    };

    if (urlSlug) {
      fetchData();
    }
  }, [communityNid, urlSlug, mode]);

  useEffect(() => {
    if (photoGalleryData && virtualTourData && videoData) {
      const gotPhotos = photoGalleryData?.length > 0;
      const gotTours = virtualTourData?.length > 0;
      const gotVideos = videoData?.length > 0;

      // Build the nav if we've got multiple types of content.
      const contentLengths = [
        photoGalleryData?.length,
        virtualTourData?.length,
        videoData?.length,
      ];
      const filteredContentLengths = contentLengths.filter(
        (length) => length > 0,
      );
      if (filteredContentLengths?.length > 1) {
        const nav = (
          <div className="filterBtn">
            {gotPhotos && (
              <HorizontalNavBarItem path="/media/galleries" label="Photos" />
            )}
            {gotTours && (
              <HorizontalNavBarItem path="/media/tours" label="Tours" />
            )}
            {gotVideos && (
              <HorizontalNavBarItem path="/media/videos" label="Videos" />
            )}
          </div>
        );
        setNavMarkup(nav);
      } else {
        setNavMarkup(false);
      }

      // Show content for the first available section.
      switch (true) {
        case gotPhotos:
          history.push(`/media/galleries/${search}`);
          break;
        case gotTours:
          history.push(`/media/tours/${search}`);
          break;
        case gotVideos:
          history.push(`/media/videos/${search}`);
          break;
        default:
          break;
      }
    }
  }, [
    photoGalleryData,
    virtualTourData,
    videoData,
    history,
    search,
  ]);

  // Redirect to first available section when user clicks on Media nav link a second time.
  useEffect(() => {
    if (
      location.pathname === '/media'
      && photoGalleryData
      && virtualTourData
      && videoData
    ) {
      const gotPhotos = photoGalleryData?.length > 0;
      const gotTours = virtualTourData?.length > 0;
      const gotVideos = videoData?.length > 0;

      // Show content for the first available section.
      switch (true) {
        case gotPhotos:
          history.push(`/media/galleries/${search}`);
          break;
        case gotTours:
          history.push(`/media/tours/${search}`);
          break;
        case gotVideos:
          history.push(`/media/videos/${search}`);
          break;
        default:
          break;
      }
    }
  }, [
    location.pathname,
    photoGalleryData,
    virtualTourData,
    videoData,
    history,
    search,
  ]);

  // Loading until we have all three data arrays (null = not yet fetched).
  const isLoading = photoGalleryData === null
    || virtualTourData === null
    || videoData === null;

  return (
    <div className="content media">

      {isLoading && <MediaPageNavSkeleton />}
      {!isLoading && navMarkup}

      {isLoading && <MediaPageSkeleton />}

      <Route
        render={({ location }) => (
          <FadingContainer>
            <div className="gridContainer">
              <Switch location={location}>
                <Route
                  path="/media/galleries"
                  render={() => <MediaPagePhotoGalleryList items={photoGalleryData} />}
                />
                <Route
                  path="/media/videos"
                  render={() => <MediaPageVideoList items={videoData} />}
                />
                <Route
                  path="/media/tours"
                  render={() => <MediaPageVirtualTourList items={virtualTourData} />}
                />
              </Switch>
            </div>
          </FadingContainer>
        )}
      />
    </div>
  );
};

export default MediaPage;
